import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, Upload, Globe, Phone, MapPin, Clock, Tag, 
  Instagram, Facebook, Twitter, Plus, X, CheckCircle,
  Loader2, AlertCircle, Building2, Image as ImageIcon
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' }
];

const BUSINESS_TYPES = [
  'Restaurante', 'Cafetería', 'Bar', 'Panadería', 'Pastelería',
  'Peluquería', 'Barbería', 'Spa', 'Gimnasio', 'Clínica',
  'Tienda de ropa', 'Zapatería', 'Joyería', 'Floristería',
  'Supermercado', 'Frutería', 'Carnicería', 'Pescadería',
  'Ferretería', 'Papelería', 'Librería', 'Electrónica',
  'Veterinaria', 'Fotografía', 'Inmobiliaria', 'Otro'
];

export const BusinessProfileEditor = ({ user, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [validatingVat, setValidatingVat] = useState(false);
  const [vatValidation, setVatValidation] = useState(null);
  
  const [profile, setProfile] = useState({
    description: '',
    businessType: '',
    logo: '',
    coverImage: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    website: '',
    socialLinks: {
      instagram: '',
      facebook: '',
      twitter: ''
    },
    schedule: {},
    services: [],
    products: [],
    vatNumber: ''
  });
  
  const [newService, setNewService] = useState('');
  const [newProduct, setNewProduct] = useState('');

  useEffect(() => {
    if (user) {
      setProfile({
        description: user.description || '',
        businessType: user.businessType || '',
        logo: user.logo || '',
        coverImage: user.coverImage || '',
        address: user.address || '',
        city: user.city || '',
        postalCode: user.postalCode || '',
        phone: user.phone || '',
        website: user.website || '',
        socialLinks: user.socialLinks || { instagram: '', facebook: '', twitter: '' },
        schedule: user.schedule || {},
        services: user.services || [],
        products: user.products || [],
        vatNumber: user.vat_number || user.vatNumber || ''
      });
      
      if (user.vatValidated) {
        setVatValidation({ valid: true, name: user.vatCompanyName });
      }
    }
  }, [user]);

  const handleScheduleChange = (day, value) => {
    setProfile(prev => ({
      ...prev,
      schedule: { ...prev.schedule, [day]: value }
    }));
  };

  const addService = () => {
    if (newService.trim()) {
      setProfile(prev => ({
        ...prev,
        services: [...prev.services, newService.trim()]
      }));
      setNewService('');
    }
  };

  const removeService = (index) => {
    setProfile(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const addProduct = () => {
    if (newProduct.trim()) {
      setProfile(prev => ({
        ...prev,
        products: [...prev.products, newProduct.trim()]
      }));
      setNewProduct('');
    }
  };

  const removeProduct = (index) => {
    setProfile(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const validateVat = async () => {
    if (!profile.vatNumber) {
      toast.error('Introduce un NIF-IVA para validar');
      return;
    }
    
    setValidatingVat(true);
    setVatValidation(null);
    
    try {
      const { data } = await axios.post(`${API}/directory/validate-vat`, {
        vat_number: profile.vatNumber,
        business_id: user?.id
      });
      
      setVatValidation(data);
      
      if (data.valid) {
        toast.success('NIF-IVA validado correctamente');
      } else {
        toast.error(data.error || 'NIF-IVA no válido');
      }
    } catch (err) {
      toast.error('Error al validar el NIF-IVA');
    } finally {
      setValidatingVat(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { data } = await axios.put(`${API}/directory/businesses/${user.id}/profile`, profile);
      
      // Update local storage
      const updatedUser = { ...user, ...profile };
      
      // Check if VAT was automatically validated
      if (data.vat_validation) {
        if (data.vat_validation.valid) {
          updatedUser.vatValidated = true;
          updatedUser.vatCompanyName = data.vat_validation.name;
          setVatValidation(data.vat_validation);
          toast.success('Perfil y NIF-IVA validado correctamente');
        } else {
          updatedUser.vatValidated = false;
          setVatValidation(data.vat_validation);
          toast.warning('Perfil guardado. NIF-IVA no pudo ser validado: ' + data.vat_validation.error);
        }
      } else {
        toast.success('Perfil actualizado correctamente');
      }
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      onUpdate?.(updatedUser);
    } catch (err) {
      toast.error('Error al guardar el perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Perfil Público</h2>
          <p className="text-muted-foreground">
            Completa tu perfil para aparecer en el directorio de negocios
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Images Section */}
        <div className="bg-card rounded-2xl p-6 border border-border/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-local-sage" />
            Imágenes
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Logo</Label>
              <Input
                type="url"
                placeholder="https://ejemplo.com/logo.png"
                value={profile.logo}
                onChange={(e) => setProfile({ ...profile, logo: e.target.value })}
                className="rounded-xl"
              />
              {profile.logo && (
                <div className="mt-2 w-20 h-20 rounded-xl overflow-hidden bg-muted">
                  <img src={profile.logo} alt="Logo" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            
            <div>
              <Label className="mb-2 block">Imagen de Portada</Label>
              <Input
                type="url"
                placeholder="https://ejemplo.com/portada.jpg"
                value={profile.coverImage}
                onChange={(e) => setProfile({ ...profile, coverImage: e.target.value })}
                className="rounded-xl"
              />
              {profile.coverImage && (
                <div className="mt-2 h-24 rounded-xl overflow-hidden bg-muted">
                  <img src={profile.coverImage} alt="Portada" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-card rounded-2xl p-6 border border-border/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-local-sage" />
            Información Básica
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Tipo de Negocio</Label>
              <select
                value={profile.businessType}
                onChange={(e) => setProfile({ ...profile, businessType: e.target.value })}
                className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm"
              >
                <option value="">Selecciona un tipo</option>
                {BUSINESS_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="mb-2 block">Teléfono</Label>
              <Input
                type="tel"
                placeholder="+34 666 123 456"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>
          
          <div className="mt-4">
            <Label className="mb-2 block">Descripción</Label>
            <Textarea
              placeholder="Describe tu negocio en pocas palabras..."
              value={profile.description}
              onChange={(e) => setProfile({ ...profile, description: e.target.value })}
              className="rounded-xl min-h-[120px]"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {profile.description.length}/500 caracteres
            </p>
          </div>
        </div>

        {/* Address */}
        <div className="bg-card rounded-2xl p-6 border border-border/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-local-sage" />
            Dirección
          </h3>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="mb-2 block">Dirección</Label>
              <Input
                placeholder="Calle, número, piso..."
                value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label className="mb-2 block">Código Postal</Label>
              <Input
                placeholder="28001"
                value={profile.postalCode}
                onChange={(e) => setProfile({ ...profile, postalCode: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="mb-2 block">Ciudad</Label>
              <Input
                placeholder="Madrid"
                value={profile.city}
                onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* VAT Validation */}
        <div className="bg-card rounded-2xl p-6 border border-border/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-local-sage" />
            NIF-IVA (Validación VIES)
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Valida tu NIF-IVA para acceder a beneficios fiscales en transacciones B2B dentro de la UE.
          </p>
          
          <div className="flex gap-3">
            <Input
              placeholder="ES12345678A"
              value={profile.vatNumber}
              onChange={(e) => setProfile({ ...profile, vatNumber: e.target.value.toUpperCase() })}
              className="rounded-xl flex-1"
            />
            <Button
              type="button"
              onClick={validateVat}
              disabled={validatingVat || !profile.vatNumber}
              variant="outline"
              className="rounded-xl"
            >
              {validatingVat ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Validar'
              )}
            </Button>
          </div>
          
          {vatValidation && (
            <div className={`mt-3 p-3 rounded-xl ${
              vatValidation.valid 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
            }`}>
              <div className="flex items-start gap-2">
                {vatValidation.valid ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <div>
                  {vatValidation.valid ? (
                    <>
                      <p className="font-medium">NIF-IVA válido</p>
                      {vatValidation.name && (
                        <p className="text-sm">{vatValidation.name}</p>
                      )}
                    </>
                  ) : (
                    <p>{vatValidation.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Schedule */}
        <div className="bg-card rounded-2xl p-6 border border-border/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-local-sage" />
            Horario de Apertura
          </h3>
          
          <div className="space-y-3">
            {DAYS.map(day => (
              <div key={day.key} className="flex items-center gap-4">
                <span className="w-24 font-medium">{day.label}</span>
                <Input
                  placeholder="09:00 - 21:00 (o 'Cerrado')"
                  value={profile.schedule[day.key] || ''}
                  onChange={(e) => handleScheduleChange(day.key, e.target.value)}
                  className="rounded-xl flex-1"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Web & Social */}
        <div className="bg-card rounded-2xl p-6 border border-border/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-local-sage" />
            Web y Redes Sociales
          </h3>
          
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Página Web</Label>
              <Input
                type="url"
                placeholder="https://www.tunegocio.com"
                value={profile.website}
                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                className="rounded-xl"
              />
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <Instagram className="w-4 h-4" /> Instagram
                </Label>
                <Input
                  placeholder="https://instagram.com/tunegocio"
                  value={profile.socialLinks.instagram}
                  onChange={(e) => setProfile({
                    ...profile,
                    socialLinks: { ...profile.socialLinks, instagram: e.target.value }
                  })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <Facebook className="w-4 h-4" /> Facebook
                </Label>
                <Input
                  placeholder="https://facebook.com/tunegocio"
                  value={profile.socialLinks.facebook}
                  onChange={(e) => setProfile({
                    ...profile,
                    socialLinks: { ...profile.socialLinks, facebook: e.target.value }
                  })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <Twitter className="w-4 h-4" /> Twitter/X
                </Label>
                <Input
                  placeholder="https://twitter.com/tunegocio"
                  value={profile.socialLinks.twitter}
                  onChange={(e) => setProfile({
                    ...profile,
                    socialLinks: { ...profile.socialLinks, twitter: e.target.value }
                  })}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Services & Products */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card rounded-2xl p-6 border border-border/50">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-local-sage" />
              Servicios
            </h3>
            
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Añadir servicio..."
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                className="rounded-xl"
              />
              <Button type="button" onClick={addService} size="icon" className="rounded-xl">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-2">
              {profile.services.map((service, i) => (
                <div key={i} className="flex items-center justify-between bg-muted/50 rounded-xl px-4 py-2">
                  <span>{service}</span>
                  <button type="button" onClick={() => removeService(i)} className="text-muted-foreground hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-2xl p-6 border border-border/50">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-local-sage" />
              Productos
            </h3>
            
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Añadir producto..."
                value={newProduct}
                onChange={(e) => setNewProduct(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addProduct())}
                className="rounded-xl"
              />
              <Button type="button" onClick={addProduct} size="icon" className="rounded-xl">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-2">
              {profile.products.map((product, i) => (
                <div key={i} className="flex items-center justify-between bg-muted/50 rounded-xl px-4 py-2">
                  <span>{product}</span>
                  <button type="button" onClick={() => removeProduct(i)} className="text-muted-foreground hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-local-sage hover:bg-local-sage/90 rounded-xl gap-2 px-8"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Guardar Perfil
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default BusinessProfileEditor;
